import { useState, useEffect } from 'react'
import { Search, Filter, X } from 'lucide-react'
import { ProjetoFilters as FiltersType } from '../tipos/projeto'
import { useAnalistasSMN, useAnalistasDDP, useStatusProjeto } from '../hooks/useProjeto'
import { useUsuario } from '../../../contexts/UsuarioContext'

interface ProjetoFiltersProps {
  onSearch: (filters: FiltersType) => void
}

export function ProjetoFilters({ onSearch }: ProjetoFiltersProps) {
  const { usuario } = useUsuario()
  const isAdmin = usuario?.papeis?.includes('1') ?? false
  const isAnalista = !isAdmin && (usuario?.papeis?.includes('2') ?? false)
  const pessoaId = usuario?.pessoaId

  const [filters, setFilters] = useState<FiltersType>({
    search: '',
    analistaSMNId: undefined,
    analistaDDPId: undefined,
    statusCodigo: undefined,
  })

  // Buscar dados de referência
  const { data: analistasSMN } = useAnalistasSMN()
  const { data: analistasDDP } = useAnalistasDDP()
  const { data: statusList } = useStatusProjeto()

  // Quando usuário for analista, definir filtro automático e notificar
  useEffect(() => {
    if (isAnalista && pessoaId) {
      const newFilters: FiltersType = {
        search: '',
        analistaSMNId: pessoaId,
        analistaDDPId: undefined,
      }
      setFilters(newFilters)
      onSearch(newFilters)
    }
  }, [isAnalista, pessoaId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSearch(filters)
  }

  const handleClear = () => {
    const clearedFilters: FiltersType = {
      search: '',
      analistaSMNId: isAnalista && pessoaId ? pessoaId : undefined,
      analistaDDPId: undefined,
      statusCodigo: undefined,
    }
    setFilters(clearedFilters)
    onSearch(clearedFilters)
  }

  const handleInputChange = (field: keyof FiltersType, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value || undefined,
    }))
  }

  return (
    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-theme-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-brand-500 to-brand-600 rounded-lg flex items-center justify-center">
            <Filter className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Filtros de Busca</h3>
            <p className="text-sm text-gray-600">Encontre projetos rapidamente</p>
          </div>
        </div>
        <button
          onClick={handleClear}
          className="px-3 py-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-200 font-medium"
        >
          <X className="w-4 h-4 mr-2 inline" />
          Limpar
        </button>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Termo de Busca
          </label>
          <div className="relative">
            <input
              type="text"
              value={filters.search || ''}
              onChange={(e) => handleInputChange('search', e.target.value)}
              className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
              placeholder="Buscar por nome ou número do ticket..."
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Analista SMN</label>
          <select
            value={filters.analistaSMNId || ''}
            onChange={(e) => handleInputChange('analistaSMNId', e.target.value ? parseInt(e.target.value) : undefined)}
            disabled={isAnalista}
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 ${isAnalista ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          >
            <option value="">Todos os analistas SMN</option>
            {analistasSMN?.map((analista: any) => {
              const idPessoa = analista.id || analista.idPessoa || analista.IdPessoa || 0
              const nome = analista.nome || analista.Nome || 'Nome não encontrado'
              return (
                <option key={idPessoa} value={idPessoa}>
                  {nome}
                </option>
              )
            })}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Analista DDP</label>
          <select
            value={filters.analistaDDPId || ''}
            onChange={(e) => handleInputChange('analistaDDPId', e.target.value ? parseInt(e.target.value) : undefined)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
          >
            <option value="">Todos os analistas DDP</option>
            {analistasDDP?.map((analista: any) => {
              const idPessoa = analista.id || analista.idPessoa || analista.IdPessoa || 0
              const nome = analista.nome || analista.Nome || 'Nome não encontrado'
              return (
                <option key={idPessoa} value={idPessoa}>
                  {nome}
                </option>
              )
            })}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <select
            value={filters.statusCodigo ?? ''}
            onChange={(e) => handleInputChange('statusCodigo', e.target.value ? parseInt(e.target.value) : undefined)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
          >
            <option value="">Todos os status</option>
            {statusList?.map((s: any) => (
              <option key={s.codigo} value={s.codigo}>{s.descricao}</option>
            ))}
          </select>
        </div>

        <div className="md:col-span-4 flex justify-end">
          <button
            type="submit"
            className="px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 font-medium"
          >
            <Search className="w-4 h-4 mr-2 inline" />
            Buscar Projetos
          </button>
        </div>
      </form>
    </div>
  )
}
